import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { i18n } from "../../translate/i18n";
import { erroUrlMidiaCabecalho } from "./campaignRules";

const t = (k, o) => i18n.t(`aeros.campaign.template.${k}`, o);

// skipOnVariables: o valor interpolado ("{{name}}") nao pode ser
// re-interpolado pelo i18next 19, senao o placeholder some do texto.
const SKIP = { interpolation: { skipOnVariables: true } };

export const rotuloDoParametro = (token) =>
  t("paramLabel", { token: `{{${token}}}`, ...SKIP });

export const textoMisturaFormatos = () =>
  t("mixedFormats", { numerada: "{{1}}", nomeada: "{{nome}}", ...SKIP });

// `accept` do seletor de arquivo por formato de cabecalho — mesma regra da
// Meta que o backend aplica em TemplateMediaUpload. Vazio = nao restringe.
const ACCEPT_POR_FORMATO = {
  IMAGE: "image/jpeg,image/png",
  VIDEO: "video/mp4",
  DOCUMENT: "application/pdf",
};

export const aceitaArquivoDoHeader = (formato) =>
  ACCEPT_POR_FORMATO[String(formato || "").toUpperCase()] || "";

export const textoDaPrevia = (template, params) =>
  t("preview", { texto: previa(template, params), ...SKIP });

export const rotuloDoTemplate = (template) => {
  const base = `${template?.shortcode} (${template?.language})`;
  return template?.headerMidia ? `${base} — (${t("mediaBadge")})` : base;
};

// So e troca real de conexao quando ja havia uma conexao definida antes e ela
// mudou para outra. A primeira definicao (null -> id) e hidratacao de uma
// campanha existente (cujo template/params ja vem no mesmo payload) ou
// selecao inicial, e nao deve apagar template/params.
export const deveLimparTemplateNaTrocaDeConexao = (
  whatsappIdAnterior,
  whatsappIdAtual
) => whatsappIdAnterior !== null && whatsappIdAnterior !== whatsappIdAtual;

// Os placeholders NAO sao recalculados aqui. Vem do backend, no campo
// `variaveisTokens` de cada template, que os le do texto do BODY na ordem em
// que a Meta espera os parametros. Uma segunda implementacao em JavaScript
// divergia da do backend (o `example` da Meta as vezes declara mais
// placeholders do que o texto usa) e o salvamento era recusado com "espera N e
// foram informados M" — erro impossivel de satisfazer pela tela.
//
// Os tokens tambem podem ser nomeados ({{name}}), nao so numerados ({{1}}), e e
// por isso que sao uma lista de string e nao uma contagem: o rotulo do campo
// mostra o placeholder real.
export const tokensDoTemplate = (template) =>
  Array.isArray(template?.variaveisTokens) ? template.variaveisTokens : [];

export const textoDoBody = (components) => {
  const lista = Array.isArray(components) ? components : [];
  const body = lista.find((c) => (c?.type || "").toUpperCase() === "BODY");
  return body?.text || "";
};

const EXEMPLO = {
  "{nome}": "Joao da Silva",
  "{numero}": "5531999998888",
  "{email}": "joao@exemplo.com",
};

const escapaRegex = (valor) => String(valor).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const previa = (template, params) => {
  const tokens = tokensDoTemplate(template);

  return tokens.reduce((texto, token, indice) => {
    const valor = params[indice];

    return texto.replace(
      new RegExp(`{{\\s*${escapaRegex(token)}\\s*}}`, "g"),
      EXEMPLO[valor] || valor || `{{${token}}}`
    );
  }, textoDoBody(template?.components));
};

const TemplateFields = ({
  templates,
  totalAprovados = 0,
  quickMessageId,
  templateParams,
  onChangeTemplate,
  onChangeParam,
  templateHeaderMediaUrl = "",
  onChangeHeaderMediaUrl,
  onUploadHeaderMedia,
  enviandoMidia = false,
  disabled,
}) => {
  const [urlTocada, setUrlTocada] = useState(false);
  const inputArquivo = useRef(null);
  useEffect(() => {
    setUrlTocada(false);
  }, [quickMessageId]);

  if (!templates.length) {
    // Dois estados vazios diferentes. Mandar sincronizar quando os templates
    // ja estao la, e apenas nao servem para campanha, e mandar o usuario
    // repetir uma acao que nao muda nada.
    return (
      <Grid xs={12} item>
        <Alert severity="warning">
          {totalAprovados > 0
            ? t("emptyNoneUsable", { count: totalAprovados })
            : t("emptySync")}
        </Alert>
      </Grid>
    );
  }

  const selecionado = templates.find((t) => t.id === quickMessageId);
  const tokens = tokensDoTemplate(selecionado);
  const misturaFormatos = selecionado?.tipoVariaveis === "misto";
  const omitidos = Math.max(0, totalAprovados - templates.length);
  const erroUrl = erroUrlMidiaCabecalho(selecionado, templateHeaderMediaUrl);
  const mostraErro = urlTocada && Boolean(erroUrl);

  return (
    <>
      {omitidos > 0 && (
        <Grid xs={12} item>
          <Alert severity="info">{t("omitted", { count: omitidos })}</Alert>
        </Grid>
      )}

      <Grid xs={12} item>
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel id="template-selection-label">
            {t("select")}
          </InputLabel>
          <Select
            label={t("select")}
            labelId="template-selection-label"
            id="quickMessageId"
            value={quickMessageId || ""}
            onChange={(e) => onChangeTemplate(e.target.value)}
            disabled={disabled}
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {rotuloDoTemplate(template)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {selecionado && (
        <Grid xs={12} item>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            {textoDoBody(selecionado.components)}
          </Typography>

          {selecionado.headerMidia && (
            <Box mb={2}>
              <TextField
                fullWidth
                required
                type="url"
                variant="outlined"
                margin="dense"
                id="templateHeaderMediaUrl"
                label={t("mediaUrlLabel", { tipo: selecionado.headerMidia })}
                value={templateHeaderMediaUrl || ""}
                onChange={(e) => onChangeHeaderMediaUrl(e.target.value)}
                disabled={disabled}
                onBlur={() => setUrlTocada(true)}
                error={mostraErro}
                helperText={
                  mostraErro && erroUrl === "required"
                    ? t("mediaUrlRequired")
                    : mostraErro && erroUrl === "invalid"
                    ? t("mediaUrlInvalid")
                    : t("mediaUrlHelp")
                }
                inputProps={{ inputMode: "url", autoCapitalize: "none", spellCheck: false }}
              />
              {onUploadHeaderMedia && (
                <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                  <input
                    ref={inputArquivo}
                    type="file"
                    accept={aceitaArquivoDoHeader(selecionado.headerMidia)}
                    style={{ display: "none" }}
                    data-testid="templateHeaderMediaFile"
                    onChange={(e) => {
                      const arquivo = e.target.files && e.target.files[0];
                      e.target.value = "";
                      if (arquivo) onUploadHeaderMedia(arquivo, selecionado.headerMidia);
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={disabled || enviandoMidia}
                    onClick={() => inputArquivo.current && inputArquivo.current.click()}
                    style={{ minHeight: 44 }}
                  >
                    {enviandoMidia ? t("uploading") : t("uploadButton")}
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    {t(`uploadHint.${selecionado.headerMidia}`)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {misturaFormatos && (
            <Box mb={2}>
              <Alert severity="error">
                {textoMisturaFormatos()}
              </Alert>
            </Box>
          )}

          <Grid spacing={2} container>
            {tokens.map((token, indice) => (
              <Grid xs={12} md={6} item key={token}>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label={rotuloDoParametro(token)}
                  value={templateParams[indice] || ""}
                  onChange={(e) => onChangeParam(indice, e.target.value)}
                  disabled={disabled || misturaFormatos}
                  helperText={t("paramHelp")}
                />
              </Grid>
            ))}
          </Grid>

          <Box mt={2}>
            <Alert severity="info">
              {textoDaPrevia(selecionado, templateParams)}
            </Alert>
          </Box>
        </Grid>
      )}
    </>
  );
};

export default TemplateFields;
